
import { v4 as uuidv4 } from 'uuid';
import { Category } from '@/types/assessment';
import { createServiceClient } from '@/services/migration/serviceClient';

// Function to save categories, questions, and answers to the database
export const saveCategoriesQuestionsAnswers = async (assessmentId: string, categories: Category[]) => {
  try {
    // Use service client to bypass RLS policies
    const serviceClient = createServiceClient();
    
    // First, get all existing categories and questions for this assessment
    const { data: existingCategories } = await serviceClient
      .from('categories')
      .select('id, questions(id)')
      .eq('assessment_id', assessmentId);
    
    // Build sets of IDs that should remain
    const categoryIdsToKeep = new Set<string>();
    const questionIdsToKeep = new Set<string>();
    
    categories.forEach(category => {
      // Only keep real UUIDs (not temp IDs)
      if (category.id.includes('-') && category.id.length === 36) {
        categoryIdsToKeep.add(category.id);
      }
      category.questions.forEach(question => {
        if (question.id.includes('-') && question.id.length === 36) {
          questionIdsToKeep.add(question.id);
        }
      });
    });
    
    // Delete categories that no longer exist
    if (existingCategories) {
      const categoryIdsToDelete = existingCategories
        .map(c => c.id)
        .filter(id => !categoryIdsToKeep.has(id));
      
      if (categoryIdsToDelete.length > 0) {
        await serviceClient
          .from('categories')
          .delete()
          .in('id', categoryIdsToDelete);
      }
      
      // Delete questions that no longer exist
      const existingQuestionIds: string[] = [];
      existingCategories.forEach((cat: any) => {
        if (cat.questions) {
          cat.questions.forEach((q: any) => existingQuestionIds.push(q.id));
        }
      });
      
      const questionIdsToDelete = existingQuestionIds.filter(id => !questionIdsToKeep.has(id));
      
      if (questionIdsToDelete.length > 0) {
        // First delete associated answers
        await serviceClient
          .from('answers')
          .delete()
          .in('question_id', questionIdsToDelete);
        
        // Then delete the questions
        await serviceClient
          .from('questions')
          .delete()
          .in('id', questionIdsToDelete);
      }
    }
    
    // Process each category
    for (let categoryIndex = 0; categoryIndex < categories.length; categoryIndex++) {
      const category = categories[categoryIndex];
      const isNewCategory = !category.id.includes('-') || category.id.length !== 36;
      
      // Insert or update category
      let categoryId = category.id;
      if (isNewCategory) {
        categoryId = uuidv4();
      }
      
      const { error: categoryError } = await serviceClient
        .from('categories')
        .upsert({
          id: categoryId,
          assessment_id: assessmentId,
          name: category.name,
          description: category.description,
          order_index: categoryIndex
        }, {
          onConflict: 'id'
        });
        
      if (categoryError) throw categoryError;
      
      // Process each question in this category
      for (let questionIndex = 0; questionIndex < category.questions.length; questionIndex++) {
        const question = category.questions[questionIndex];
        const isNewQuestion = !question.id.includes('-') || question.id.length !== 36;
        
        // Insert or update question
        let questionId = question.id;
        if (isNewQuestion) {
          questionId = uuidv4();
        }
        
        const { error: questionError } = await serviceClient
          .from('questions')
          .upsert({
            id: questionId,
            category_id: categoryId,
            text: question.text,
            explanation: question.explanation,
            answer_type: question.answerType,
            required: question.required,
            premium: question.premium,
            disabled: question.disabled || false,
            order_index: questionIndex
          }, {
            onConflict: 'id'
          });
          
        if (questionError) throw questionError;
        
        // Delete any existing answers for this question before inserting new ones
        if (!isNewQuestion) {
          const { error: deleteError } = await serviceClient
            .from('answers')
            .delete()
            .eq('question_id', questionId);
            
          if (deleteError) throw deleteError;
        }
        
        // Insert all answers for this question
        for (const answer of question.answers) {
          const { error: answerError } = await serviceClient
            .from('answers')
            .insert({
              id: uuidv4(),
              question_id: questionId,
              text: answer.text,
              value: answer.value,
              recommended_actions: JSON.stringify(answer.recommendedActions || [])
            });
            
          if (answerError) throw answerError;
        }
      }
    }
  } catch (error) {
    console.error('Error saving assessment structure:', error);
    throw error;
  }
};
